/**
 * Deep link handler for duel invite codes.
 * URL: /duel/invite/{code}
 * If logged in → accept invite → navigate to duel.
 * If not logged in → redirect to auth, then back here.
 */

import { useEffect, useState } from 'react';
import { View, Text, ActivityIndicator, StyleSheet, Platform } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { acceptDuelInvite, getDuelInviteByCode } from '@/lib/duelInvites';

export default function DuelInviteHandler() {
  const { theme } = useTheme();
  const { profile, session } = useAuth();
  const router = useRouter();
  const { code } = useLocalSearchParams();
  const inviteCode = typeof code === 'string' ? code : '';

  const [status, setStatus] = useState<'loading' | 'error' | 'accepted'>('loading');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!inviteCode) {
      setStatus('error');
      setErrorMsg('Invalid invite link');
      return;
    }

    if (!session) {
      // Not logged in — redirect to auth with return path
      router.replace(`/gate?redirect=/duel/invite/${inviteCode}`);
      return;
    }

    if (!profile?.id) return;

    (async () => {
      try {
        const invite = await getDuelInviteByCode(inviteCode);
        if (!invite) {
          setStatus('error');
          setErrorMsg('Invite not found or expired');
          return;
        }

        if (invite.accepted_by) {
          // Already accepted — just navigate to the duel
          router.replace(`/duels/${invite.duel_id}`);
          return;
        }

        const { duelId } = await acceptDuelInvite(inviteCode, profile.id);
        setStatus('accepted');
        setTimeout(() => router.replace(`/duels/${duelId}`), 500);
      } catch (err: unknown) {
        setStatus('error');
        setErrorMsg(err instanceof Error ? err.message : 'Failed to accept invite');
      }
    })();
  }, [inviteCode, session, profile?.id, router]);

  const c = theme.colors;

  return (
    <>
      <View style={[styles.container, { backgroundColor: c.background }]}>
      {status === 'loading' && (
        <>
          <ActivityIndicator size="large" color={c.accent} />
          <Text style={[styles.text, { color: c.text }]}>Accepting challenge…</Text>
        </>
      )}
      {status === 'accepted' && (
        <Text style={[styles.text, { color: c.accent }]}>Challenge accepted! ⚔️</Text>
      )}
      {status === 'error' && (
        <Text style={[styles.text, { color: c.error ?? '#ff4444' }]}>{errorMsg}</Text>
      )}
    </View>
    </>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 },
  text: { fontSize: 18, fontFamily: 'Inter_600SemiBold', marginTop: 16, textAlign: 'center' },
});
